/** @jsx jsx */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { Button, Box, jsx, Text } from 'theme-ui'
import { useAuth } from '@chordcommerce/gatsby-theme-autonomy'
import Dropdown from '~/components/Generic/Dropdown'
import ListingAddToCartButton from '~/components/Listing/AddToCart/Button'
import { useCanClub } from '~/hooks/components/use-can-club'
import { getLoginUrl } from '~/utils/auth'
import { toUsdCurrency } from '~/utils/formatters'
import { getKitBottleCount, getKitCanCount } from '~/utils/kits'
import { getAvailablePackKits, getDiscountedPrice } from '~/utils/packs'

const SITEWIDE_DISCOUNT = process.env.GATSBY_SITEWIDE_DISCOUNT

const SITEWIDE_DISCOUNT_CLUB = process.env.GATSBY_SITEWIDE_DISCOUNT_CLUB

const PackAddToCartDropdown = ({ pack }) => {
  const { isLoggedIn } = useAuth()
  const { clubDiscount } = useCanClub()
  const { canClubOnly, comingSoon, soldOut } = pack
  const availableKits = getAvailablePackKits(pack)

  const options = useMemo(
    () =>
      availableKits.map((kit) => {
        const canCount = getKitCanCount(kit)
        const bottleCount = getKitBottleCount(kit)
        const price = toUsdCurrency(kit.price)

        return {
          ...kit,
          title: `${canCount} cans`,
          subtitle: `= ${bottleCount} bottles`,
          additional: price,
        }
      }),
    [availableKits]
  )

  const [currentOption, setCurrentOption] = useState(options[0])
  const { sku } = currentOption

  const [discountedOptions, setDiscountedOptions] = useState([])

  useEffect(() => {
    if (discountedOptions.length > 0) return

    const discountOptions = (discountPercentage, includeStruck) => {
      const newOptions = [...options]

      for (let i = 0; i < newOptions.length; i++) {
        if (includeStruck) {
          newOptions[i].additionalStruck = newOptions[i].additional
        }

        newOptions[i].additional = toUsdCurrency(
          getDiscountedPrice(discountPercentage, availableKits[i].price)
        )
      }

      setDiscountedOptions(newOptions)
      setCurrentOption(newOptions[0])
    }

    if (clubDiscount) {
      if (SITEWIDE_DISCOUNT_CLUB) {
        discountOptions(SITEWIDE_DISCOUNT_CLUB, true)
      } else {
        discountOptions(clubDiscount)
      }
    } else if (SITEWIDE_DISCOUNT) {
      discountOptions(SITEWIDE_DISCOUNT, true)
    }
  }, [availableKits, clubDiscount, discountedOptions, options])

  return (
    <Box>
      <Box mb={2}>
        <Dropdown
          ariaLabel="Available quantities"
          currentOption={currentOption}
          disabled={
            soldOut || comingSoon || (canClubOnly && clubDiscount === 0)
          }
          options={discountedOptions.length > 0 ? discountedOptions : options}
          optionsLabel={'Select quantity'}
          setOption={setCurrentOption}
        />
      </Box>
      {canClubOnly && !clubDiscount && !soldOut && (
        <Fragment>
          <Button as={Link} to="/can-club/" sx={{ width: '100%' }}>
            {'Join the club'}
          </Button>
          {!isLoggedIn && (
            <Text
              as="p"
              variant="body3"
              m={0}
              mt={2}
              sx={{ position: 'absolute' }}
            >
              Already a member?{' '}
              <Link to={`/${getLoginUrl()}/`}>
                <Text
                  as="span"
                  sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
                >
                  Log in
                </Text>
              </Link>
            </Text>
          )}
        </Fragment>
      )}
      {(!canClubOnly || (canClubOnly && clubDiscount > 0) || soldOut) && (
        <ListingAddToCartButton
          comingSoon={comingSoon}
          sku={sku}
          soldOut={soldOut}
        />
      )}
    </Box>
  )
}

export default PackAddToCartDropdown
